import { create } from 'zustand';
import { FieldMappingSummary, FieldMappingDetail } from '../types';
import { logger } from '@/features/help/utils/logger';
import { toast } from 'react-hot-toast';
import * as formBuilderApi from '../api/form-builder.services';
import { useFormBuilderStore } from '../store/form-builder.store';
import { useValidationStore } from '../store/validation.store';
import { useCrmStore } from '../store/crm.store';
import { validateGridLevelChecks } from '../utils/field-mapping-validation.utils';

export interface MappingsStore {
  // Available mappings
  mappingSummaries: FieldMappingSummary[];
  mappingDetails: FieldMappingDetail[];
  activeMappingId: string | null;
  currentFieldMappingId: string | null;
  isLoadingAvailableMappings: boolean;
  isLoadingMappingDetail: boolean;
  errorAvailableMappings: string | null;
  errorMappingDetail: string | null;

  // State for the currently selected or new mapping
  isDrafting: boolean;
  currentMappingName: string | null;
  currentMappingPrimaryObject: string | null;
  currentMappingDetails: FieldMappingDetail | null;

  // Duplicate name modal state
  duplicateNameModalOpen: boolean;
  openDuplicateNameModal: () => void;
  closeDuplicateNameModal: () => void;

  // Actions
  fetchAllMappingDetails: () => Promise<void>;
  fetchMappingDetails: (mappingId: string) => Promise<FieldMappingDetail | null>;
  setCurrentFieldMappingId: (mappingId: string | null) => void;
  setCurrentMappingName: (name: string | null) => void;
  setCurrentMappingPrimaryObject: (primaryObject: string | null) => void;

  saveAndTestCurrentMapping: () => Promise<{ gridLevelErrors: string[] }>;

  deleteFieldMapping: (mappingId: string) => Promise<void>;

  // Draft mapping actions
  startNewMappingDraft: () => void;

  // Helper selectors
  getCurrentMapping: () => FieldMappingSummary | null;
  getCurrentPrimaryObject: () => string | null;

  setMappingDetails: (details: FieldMappingDetail) => void;

  setActiveMapping: (mappingId: string) => void;
}

export const useMappingsStore = create<MappingsStore>((set, get) => ({
  mappingSummaries: [],
  mappingDetails: [],
  activeMappingId: null,
  currentFieldMappingId: null,
  isLoadingAvailableMappings: false,
  isLoadingMappingDetail: false,
  errorAvailableMappings: null,
  errorMappingDetail: null,

  isDrafting: false,
  currentMappingName: null,
  currentMappingPrimaryObject: null,
  currentMappingDetails: null,
  duplicateNameModalOpen: false,

  fetchAllMappingDetails: async () => {
    set({ isLoadingAvailableMappings: true, errorAvailableMappings: null });
    try {
      const details = await formBuilderApi.fetchAllMappingDetails();
      const summaries = details.map((d) => ({
        mapping_id: d.mapping_id,
        short_id: d.short_id,
        mapping_name: d.mapping_name,
        primary_crm_object: d.primary_crm_object,
        updated_at: d.updated_at,
      }));
      set({
        mappingDetails: details,
        mappingSummaries: summaries,
        isLoadingAvailableMappings: false,
      });
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to fetch field mappings';
      logger.error('MappingsStore', 'fetchAllMappingDetails error', { error });
      toast.error(errorMsg);
      set({ isLoadingAvailableMappings: false, errorAvailableMappings: errorMsg });
    }
  },

  fetchMappingDetails: async (mappingId: string) => {
    // This function can now pull from the cached details if available
    const existingDetail = get().mappingDetails.find((d) => d.mapping_id === mappingId);
    if (existingDetail) {
      return existingDetail;
    }

    // Fallback to API call if not found (should be rare)
    if (!mappingId) {
      set({ isLoadingMappingDetail: false });
      return null;
    }

    set({
      isLoadingMappingDetail: true,
      errorMappingDetail: null,
    });

    try {
      const details = await formBuilderApi.fetchMappingDetails(mappingId);
      set((state) => ({
        mappingDetails: [
          ...state.mappingDetails.filter((d) => d.mapping_id !== mappingId),
          details,
        ],
        isLoadingMappingDetail: false,
      }));
      return details;
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : `Failed to fetch details for mapping ${mappingId}`;
      logger.error('MappingsStore', 'fetchMappingDetails error', { error, mappingId });
      toast.error(errorMsg);
      set({
        isLoadingMappingDetail: false,
        errorMappingDetail: errorMsg,
      });
      return null;
    }
  },

  setCurrentFieldMappingId: (mappingId: string | null) => {
    if (mappingId === null) {
      set({
        currentFieldMappingId: null,
        isDrafting: false,
        currentMappingName: null,
        currentMappingPrimaryObject: null,
      });
      return;
    }

    const mapping = get().mappingSummaries.find((m) => m.mapping_id === mappingId);
    if (mapping) {
      set({
        currentFieldMappingId: mappingId,
        isDrafting: false,
        currentMappingName: mapping.mapping_name,
        currentMappingPrimaryObject: mapping.primary_crm_object,
      });
    } else {
      // Fallback or error if mapping not found, for now just set ID
      set({
        currentFieldMappingId: mappingId,
        isDrafting: false,
        currentMappingName: 'Loading...',
        currentMappingPrimaryObject: null,
      });
    }
  },

  setCurrentMappingName: (name: string | null) => {
    set({ currentMappingName: name });
  },

  setCurrentMappingPrimaryObject: (primaryObject: string | null) => {
    set({ currentMappingPrimaryObject: primaryObject });
  },

  saveAndTestCurrentMapping: async () => {
    const {
      isDrafting,
      currentFieldMappingId,
      currentMappingName,
      currentMappingPrimaryObject,
      mappingSummaries,
    } = get();
    const { currentMappingStructure } = useFormBuilderStore.getState();
    const { setTestResults } = useValidationStore.getState();
    const { isAddressPicklistOrgEnabled, crmFieldsForObject } = useCrmStore.getState();

    if (!currentMappingStructure || !currentMappingPrimaryObject || !currentMappingName) {
      toast.error('Please select a primary object and provide a mapping name before saving.');
      return { gridLevelErrors: [] };
    }

    // Check for duplicate mapping names (case-insensitive)
    const nameExists = mappingSummaries.some(
      (m) =>
        m.mapping_name.toLowerCase() === currentMappingName.toLowerCase() &&
        m.mapping_id !== currentFieldMappingId
    );

    if (nameExists) {
      get().openDuplicateNameModal();
      return { gridLevelErrors: [] };
    }

    // Get the full list of CRM fields for the current primary object
    const allCrmFields = crmFieldsForObject[currentMappingPrimaryObject] || [];

    if (allCrmFields.length === 0) {
      toast.error('CRM fields not loaded for the selected object. Please try refresh the page.');
      return { gridLevelErrors: [] };
    }

    // STEP 1: Perform local grid-level validation checks
    const gridLevelErrors = validateGridLevelChecks(
      currentMappingStructure,
      crmFieldsForObject,
      currentMappingPrimaryObject,
      isAddressPicklistOrgEnabled ?? false
    );

    // If grid-level validation fails, return errors to be handled by caller
    if (gridLevelErrors.length > 0) {
      return { gridLevelErrors };
    }

    // STEP 2: If local checks pass, proceed with backend "pre-flight" validation
    setTestResults({
      isOpen: true,
      results: [],
      totalMappings: 0,
      validMappings: 0,
      inProgress: true,
      saveCompleted: false,
    });

    try {
      const response = await formBuilderApi.saveAndTestFieldMappings({
        mappingId: isDrafting ? undefined : (currentFieldMappingId ?? undefined),
        mappingName: currentMappingName,
        primaryCrmObject: currentMappingPrimaryObject,
        formStructure: currentMappingStructure,
        isAddressPicklistOrgEnabled: isAddressPicklistOrgEnabled ?? false,
        allCrmFields: allCrmFields,
      });

      setTestResults({
        results: response.results,
        validMappings: response.results.filter((r) => r.isValid).length,
        totalMappings: response.results.length,
        inProgress: false,
        saveCompleted: response.saved,
      });

      if (response.validationFailed) {
        return { gridLevelErrors: [] };
      }

      if (response.saved && response.mappingId) {
        await get().fetchAllMappingDetails();
        get().setCurrentFieldMappingId(response.mappingId);
      }
    } catch (error: any) {
      logger.error('MappingsStore', 'Failed to save field mapping', {
        error,
        status: error.response?.status,
        statusText: error.response?.statusText,
      });
      setTestResults({ inProgress: false });
    }

    return { gridLevelErrors: [] };
  },

  deleteFieldMapping: async (mappingId: string) => {
    set({ isLoadingAvailableMappings: true });
    try {
      await formBuilderApi.deleteMapping(mappingId);
      set((state) => ({
        mappingSummaries: state.mappingSummaries.filter((m) => m.mapping_id !== mappingId),
        mappingDetails: state.mappingDetails.filter((d) => d.mapping_id !== mappingId),
        isLoadingAvailableMappings: false,
        ...(state.currentFieldMappingId === mappingId && {
          // If current mapping is deleted
          currentFieldMappingId: null,
        }),
      }));
      toast.success('Field mapping deleted.');

      // If no mappings left, or if current was deleted and list is not empty, select another one
      if (get().currentFieldMappingId === null && get().mappingSummaries.length > 0) {
        get().setCurrentFieldMappingId(get().mappingSummaries[0].mapping_id);
      }
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : `Failed to delete mapping ${mappingId}`;
      logger.error('MappingsStore', 'deleteFieldMapping error', { error });
      toast.error(errorMsg);
      set({ isLoadingAvailableMappings: false });
    }
  },

  startNewMappingDraft: () => {
    set({
      isDrafting: true,
      currentFieldMappingId: null,
      currentMappingName: 'New Mapping',
      currentMappingPrimaryObject: null,
    });
    // Also clear the form structure in the form builder store
    useFormBuilderStore.getState().setActiveFieldId(null);
    useFormBuilderStore.getState().loadStructureForCurrentMapping();
  },

  getCurrentMapping: () => {
    const state = get();
    if (state.currentFieldMappingId) {
      return (
        state.mappingSummaries.find((m) => m.mapping_id === state.currentFieldMappingId) || null
      );
    }
    return null;
  },

  getCurrentPrimaryObject: () => {
    return get().currentMappingPrimaryObject;
  },

  setMappingDetails: (details) => {
    set((state) => ({
      ...state,
      currentMappingDetails: {
        ...details,
        primary_crm_object: details.primary_crm_object,
      },
    }));
  },

  setActiveMapping: (mappingId: string) => {
    const mapping = get().mappingSummaries.find((m) => m.mapping_id === mappingId);
    if (mapping) {
      set({
        activeMappingId: mappingId,
        currentMappingPrimaryObject: mapping.primary_crm_object,
      });
      get().fetchMappingDetails(mappingId);
    }
  },

  openDuplicateNameModal: () => set({ duplicateNameModalOpen: true }),
  closeDuplicateNameModal: () => set({ duplicateNameModalOpen: false }),
}));
